import { Formik } from "formik";
import React, { FC, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useGetCameraConfigQuery,
  useUpdateConfigurationSingleDeviceMutation,
} from "../../../../api/network/vmApiService";
import { setSingleVMCameraCurrentConfigs } from "../../../../api/store/commonSlice";
import { CameraDeviceConfigProfile } from "../../../../api/types/Camera";
import {
  SpeedingConfiguration,
  TrafficConfiguration,
} from "../../../../api/types/CamerasAdminVM";
import svgIcons from "../../../../assets/svg/svgIcons";
import { AdminFormFieldCheckbox, AdminFormFieldRadioCheckbox } from "../../../../components/admin/formFields";
import { APP_CONFIG } from "../../../../constants/constants";
import { routeUrls } from "../../../../navigation/routeUrls";
import HeaderView from "../../components/HeaderView";
import SubHeaderView from "../../components/SubHeaderView";
import {
  trafficSignalDetectionFormInitialValues,
  trafficSignalDetectionFormValidationSchema,
} from "../../validation";
import NotifocationAndViolation from "./notification-and-violation-field";
import RollingStop from "./rolling-stop";
import SpeedCameraNotification from "./speed-camera-notification";
import Speeding from "./speeding";

const Mobility_Functions = {
  speed_camera_notification: "speedCamEnable",
  railroad_crossing_notification: "railroadCrossEnable",
  school_zone_speed_violation: "schoolZoneSpeedingEnable",
  rolling_stop: "rollingStopEnable",
};

const Speeding_Functions = {
  speeding: "speedingEnable",
};

const ScreenTrafficSignAndSignalDetection: FC = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.management.traffic_signal_detection",
  });
  const navigationTypeRef = useRef<"none" | "next" | "previous">("none");
  const { cameraId } = useParams<{ cameraId: string }>();
  const { state: deviceData } = useLocation();
  const model = deviceData?.model || "";
  const isSingleConfiguration = deviceData?.isSingleConfiguration || false;
  const isMassSingleConfiguration = deviceData?.isMassSingleConfiguration || false;
  const cameraName = deviceData?.cameraName || null;
  const cameraIds = deviceData?.cameraIds || [];
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const allCameraConfigsInStore = useSelector(
    (state: {
      commonReducer: {
        singleVMCameraCurrentConfigs: {
          [key: string]: CameraDeviceConfigProfile;
        };
      };
    }) => state.commonReducer.singleVMCameraCurrentConfigs
  );
  const thisCameraConfigInStore =
    allCameraConfigsInStore?.[cameraId as string] || {};
  const [selectedEvent, setSelectedEvent] = useState<string>("speedCamEnable");
  const [updateConfigurationSingleApiDeviceTrigger] =
    useUpdateConfigurationSingleDeviceMutation();

  const buildEventPayload = (values: any, events: string[]) => {
    const payload: Record<string, any> = {};

    events.forEach((eventField) => {
      const isEnabled = values[eventField];
      const eventKey = eventField?.replace(/Enable$/, "");

      if (isEnabled) {
        payload[eventField] = true;
        if (eventField === Mobility_Functions.speed_camera_notification) {
          payload[`${eventKey}_speed`] = values[`${eventKey}_speed`];
          payload[`${eventKey}_videoBefore`] =
            values[`${eventKey}_videoBefore`];
          payload[`${eventKey}_videoAfter`] = values[`${eventKey}_videoAfter`];
          payload[`${eventKey}_audioPreWarning`] =
            values[`${eventKey}_audioPreWarning`];
          payload[`${eventKey}_audioAlert`] = values[`${eventKey}_audioAlert`];
          payload[`${eventKey}_minorMedia`] =
            values[`${eventKey}_minorMedia`] || "snapshot";
          payload[`${eventKey}_minorGsensor`] =
            values[`${eventKey}_minorGsensor`];
          payload[`${eventKey}_moderateMedia`] =
            values[`${eventKey}_moderateMedia`] || "snapshot";
          payload[`${eventKey}_moderateGsensor`] =
            values[`${eventKey}_moderateGsensor`];
          payload[`${eventKey}_severeMedia`] =
            values[`${eventKey}_severeMedia`] || "snapshot";
          payload[`${eventKey}_severeGsensor`] =
            values[`${eventKey}_severeGsensor`];
        } else if (eventField === Mobility_Functions.rolling_stop) {
          payload[`${eventKey}_videoBefore`] =
            values[`${eventKey}_videoBefore`];
          payload[`${eventKey}_videoAfter`] = values[`${eventKey}_videoAfter`];
          payload[`${eventKey}_audioPreWarning`] =
            values[`${eventKey}_audioPreWarning`];
          payload[`${eventKey}_audioAlert`] = values[`${eventKey}_audioAlert`];
          payload[`${eventKey}_media`] =
            values[`${eventKey}_media`] || "snapshot";
        } else {
          payload[`${eventKey}_audioPreWarning`] =
            values[`${eventKey}_audioPreWarning`];
        }
      } else {
        payload[eventField] = false;
      }
    });

    return payload;
  };
  const MOBILITY_MAP_FUNCTIONS = [
    "speedCamEnable",
    "railroadCrossEnable",
    "schoolZoneSpeedingEnable",
    "rollingStopEnable",
  ];

  const SPEEDING_MAP_FUNCTIONS = ["speedingEnable"];

  const handleLeftNavigation = () => {
    navigate(
      isSingleConfiguration || isMassSingleConfiguration
        ? `${routeUrls.dashboardChildren.cameraChildren.ai_event_detection_dms}/${cameraId}`
        : routeUrls.dashboardChildren.cameraChildren.ai_event_detection_dms,
      {
        state: {
          isSingleConfiguration: isSingleConfiguration,
          isMassSingleConfiguration: isMassSingleConfiguration,
          cameraIds: cameraIds,
          model: model,
          cameraName: cameraName
        },
      }
    );
  };

  const handleRightNavigation = () => { };

  const handleNoneNavigation = () => {
    navigate(routeUrls.dashboardChildren.cameraChildren.camera_management);
  };

  const {
    data: dataSingleCameraConfig,
    isFetching: isFetchingSingleCameraConfig,
  } = useGetCameraConfigQuery(cameraId || "", {
    skip: (!isSingleConfiguration && !isMassSingleConfiguration) || !cameraId,
    refetchOnMountOrArgChange: true,
  });

  const memoizedInitialValues = React.useMemo(() => {
    const initialValues = trafficSignalDetectionFormInitialValues(
      !isSingleConfiguration && !isMassSingleConfiguration
    );
    if (!dataSingleCameraConfig?.data?.current || !cameraId)
      return initialValues;
    const {
      data: { current },
    } = dataSingleCameraConfig;
    // here we seed store with freshly fetched configuration
    setTimeout(() => {
      dispatch(
        setSingleVMCameraCurrentConfigs({
          cid: cameraId,
          profile: current,
        })
      );
    }, 100);
    return {
      ...initialValues,
      // under speeding payload
      truckModeEnable: current.speeding.truckModeEnable,
      mmfOverSpeed_audioAlert: current.speeding.mmfOverSpeed_audioAlert,
      speedingEnable: current.speeding.speedingEnable,

      // Left Menu
      speedCamEnable: current.trafficAndSign.speedCamEnable,
      rollingStopEnable: current.trafficAndSign.rollingStopEnable,
      railroadCrossEnable: current.trafficAndSign.railroadCrossEnable, // Deprectaed in VM
      schoolZoneSpeedingEnable: current.trafficAndSign.schoolZoneSpeedingEnable, // Deprectaed in VM

      // Speed Cam
      speedCam_speed: current.trafficAndSign.speedCam_speed,
      speedCam_videoBefore: current.trafficAndSign.speedCam_videoBefore,
      speedCam_videoAfter: current.trafficAndSign.speedCam_videoAfter,
      speedCam_audioPreWarning: current.trafficAndSign.speedCam_audioPreWarning,
      speedCam_audioAlert: current.trafficAndSign.speedCam_audioAlert,
      speedCam_minorMedia: current.trafficAndSign.speedCam_minorMedia,
      speedCam_minorGsensor: current.trafficAndSign.speedCam_minorGsensor,
      speedCam_moderateMedia: current.trafficAndSign.speedCam_moderateMedia,
      speedCam_moderateGsensor: current.trafficAndSign.speedCam_moderateGsensor,
      speedCam_severeMedia: current.trafficAndSign.speedCam_severeMedia,
      speedCam_severeGsensor: current.trafficAndSign.speedCam_severeGsensor,

      // rolling speed
      rollingStop_videoBefore: current.trafficAndSign.rollingStop_videoBefore,
      rollingStop_videoAfter: current.trafficAndSign.rollingStop_videoAfter,
      rollingStop_audioPreWarning:
        current.trafficAndSign.rollingStop_audioPreWarning,
      rollingStop_audioAlert: current.trafficAndSign.rollingStop_audioAlert,
      rollingStop_media: current.trafficAndSign.rollingStop_media,

      //railroad crossing
      railroadCross_audioPreWarning:
        current.trafficAndSign.railroadCross_audioPreWarning,

      //school zone speed violation
      schoolZoneSpeeding_audioPreWarning:
        current.trafficAndSign.schoolZoneSpeeding_audioPreWarning,
    };
  }, [isSingleConfiguration, isMassSingleConfiguration, dataSingleCameraConfig]);

  return (
    <Formik
      enableReinitialize={true}
      initialValues={memoizedInitialValues}
      validationSchema={trafficSignalDetectionFormValidationSchema}
      onSubmit={async (values) => {
        const navigationType = navigationTypeRef.current;
        const speedingPayload: SpeedingConfiguration = {
          speedingEnable: values.speedingEnable,
          truckModeEnable: values.truckModeEnable,
          mmfOverSpeed_audioAlert: values.mmfOverSpeed_audioAlert || "severe",
        };
        const trafficPayload: TrafficConfiguration = buildEventPayload(
          values,
          MOBILITY_MAP_FUNCTIONS
        );

        const updateAllCameras = async () => {
          try {
            for (const id of cameraIds) {
              await updateConfigurationSingleApiDeviceTrigger({
                cameraId: id,
                speeding: speedingPayload,
                trafficAndSign: {
                  ...trafficPayload,
                  speedCam_speed: parseInt(
                    `${trafficPayload.speedCam_speed || 0}`
                  ),
                },
              }).unwrap();
            }
            toast.success("All cameras updated successfully");
            if (navigationType === "next") {
              handleRightNavigation();
            } else if (navigationType === "previous") {
              handleLeftNavigation();
            } else {
              handleNoneNavigation();
            }
          } catch (err: any) {
            console.error(err);
            toast.error("Failed to update one or more cameras");
          }
        };
        if (isSingleConfiguration || isMassSingleConfiguration) {
          try {
            const data = await updateConfigurationSingleApiDeviceTrigger({
              cameraId: cameraId,
              speeding: speedingPayload,
              trafficAndSign: {
                ...trafficPayload,
                speedCam_speed: parseInt(
                  `${trafficPayload.speedCam_speed || 0}`
                ),
              },
            }).unwrap();
            if (data.message && data.result) {
              dispatch(
                setSingleVMCameraCurrentConfigs({
                  cid: cameraId as string,
                  profile: {
                    ...thisCameraConfigInStore,
                    speeding: {
                      ...thisCameraConfigInStore.speeding,
                      ...speedingPayload,
                    },
                    trafficAndSign: {
                      ...thisCameraConfigInStore.trafficAndSign,
                      ...trafficPayload,
                    },
                  },
                })
              );
              toast.success("Updated Successfully");
              if (navigationType === "next") {
                handleRightNavigation();
              } else if (navigationType === "previous") {
                handleLeftNavigation();
              } else {
                handleNoneNavigation();
              }
            } else {
              toast.error("Updation Failed");
            }
          } catch (errors: any) {
            console.error(errors);
            toast.error(errors?.join(""));
          }
        } else {
          await updateAllCameras();
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        setFieldTouched,
        isSubmitting,
        dirty,
        isValid,
        resetForm,
      }) => {
        return (
          <div className="p-4 bg-gray-100 relative">
            {isFetchingSingleCameraConfig && (
              <div className="absolute w-full h-full top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white/70 z-10 flex items-center justify-center">
                <img
                  src={svgIcons.LoadingAnimation}
                  alt=""
                  className="size-12"
                />
              </div>
            )}
            {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
              <div className="flex text-[10px]">
                <pre className="flex w-1/3 whitespace-break-spaces break-all">
                  {JSON.stringify(memoizedInitialValues, null, 2)}
                </pre>
                <pre className="flex w-1/3 whitespace-break-spaces break-all">
                  {JSON.stringify(values, null, 2)}
                </pre>
                <pre className="flex w-1/3 whitespace-break-spaces break-all max-h-72 overflow-auto">
                  {JSON.stringify(thisCameraConfigInStore, null, 2)}
                </pre>
              </div>
            )}
            <HeaderView
              heading={t("heading")}
              handleSave={() => {
                navigationTypeRef.current = "none";
                handleSubmit();
              }}
              handleSaveAndNext={() => {
                navigationTypeRef.current = "next";
                handleSubmit();
              }}
              handlePrevious={() => {
                navigationTypeRef.current = "previous";
                handleSubmit();
              }}
              handleLeftNavigation={handleLeftNavigation}
              handleRestoreToDefault={() => resetForm()}
              disabled={
                !isValid ||
                isSubmitting ||
                (!isSingleConfiguration && !isMassSingleConfiguration && cameraIds?.length < 1)
              }
              index={4}
            />
            <div className="bg-white grid grid-cols-12">
              <SubHeaderView
                isSingleConfiguration={isSingleConfiguration}
                isMassSingleConfiguration={isMassSingleConfiguration}
                noOfCamera={cameraIds?.length}
                cameraName={cameraName}
              />
              <form
                className="col-span-12 grid grid-cols-12"
                onSubmit={handleSubmit}
              >
                <div className="col-span-3 border-r">
                  <div className="w-full py-4 border-b">
                    <AdminFormFieldCheckbox
                      customWrapperClass="col-span-3 mx-2"
                      label={t("truckModeEnable")}
                      id="truckModeEnable"
                      type="checkbox"
                      name="truckModeEnable"
                      checked={values.truckModeEnable}
                      onChange={(e) => {
                        console.log(e.target);
                        const { checked } = e.target;
                        setFieldValue("truckModeEnable", checked);
                        MOBILITY_MAP_FUNCTIONS.forEach((event) => {
                            setFieldValue(event, checked);
                        });
                        SPEEDING_MAP_FUNCTIONS.forEach((event) => {
                          setFieldValue(event, checked);
                        });
                      }}
                      onBlur={handleBlur}
                      isPrimaryListItem={true}
                    />
                    {MOBILITY_MAP_FUNCTIONS?.map(
                      (mobility_function: string, index) => {
                        return (
                          <div
                            key={index}
                            className={`w-full p-2 cursor-pointer hover:bg-accent-blue-paleO66`}
                            onClick={() => setSelectedEvent(mobility_function)}
                          >
                          <AdminFormFieldRadioCheckbox
                              label={t(mobility_function)}
                              radioChecked={selectedEvent === mobility_function}
                              onRadioChange={() => setSelectedEvent(mobility_function)}
                              checked={Boolean(values[mobility_function as keyof typeof values])}
                              onCheckboxChange={() => {
                                setFieldValue(
                                  mobility_function,
                                  !Boolean(values[mobility_function as keyof typeof values])
                                );
                              }}
                              disabled={!values.truckModeEnable}
                              isRadioButton={true}
                            />
                          </div>
                        )
                      })}
                  </div>
                  
                  <div className="w-full border-b">
                    {SPEEDING_MAP_FUNCTIONS?.map(
                      (speeding_function: string, index) => {
                        return (
                          <div
                            key={index}
                            className={`w-full p-2 cursor-pointer hover:bg-accent-blue-paleO66`}
                            onClick={() => setSelectedEvent(speeding_function)}
                          >
                            <AdminFormFieldRadioCheckbox
                              label={t(speeding_function)}
                              radioChecked={selectedEvent === speeding_function}
                              onRadioChange={() => setSelectedEvent(speeding_function)}
                              checked={Boolean(values[speeding_function as keyof typeof values])}
                              onCheckboxChange={() => {
                                setFieldValue(
                                  speeding_function,
                                  !Boolean(values[speeding_function as keyof typeof values])
                                );
                              }}
                              isRadioButton={true}
                              disabled={ !values.truckModeEnable }
                            />
                          </div>
                        );
                      }
                    )}
                  </div>
                </div>
                <div
                  className={`col-span-9 grid grid-cols-6 ${Boolean(values[selectedEvent as keyof typeof values]) === false ? "pointer-events-none opacity-50" : ""}`}
                >
                  {selectedEvent ===
                    Mobility_Functions.speed_camera_notification && (
                      <SpeedCameraNotification
                        values={values}
                        errors={errors}
                        touched={touched}
                        handleChange={handleChange}
                        setFieldValue={setFieldValue}
                        handleBlur={handleBlur}
                        setFieldTouched={setFieldTouched}
                        selectedField={selectedEvent}
                      />
                    )}
                  {selectedEvent === Mobility_Functions.rolling_stop && (
                    <RollingStop
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      setFieldValue={setFieldValue}
                      handleBlur={handleBlur}
                      setFieldTouched={setFieldTouched}
                      selectedField={selectedEvent}
                    />
                  )}
                  {(selectedEvent ===
                    Mobility_Functions.railroad_crossing_notification ||
                    selectedEvent ===
                    Mobility_Functions.school_zone_speed_violation) && (
                      <NotifocationAndViolation
                        values={values}
                        errors={errors}
                        touched={touched}
                        handleChange={handleChange}
                        setFieldValue={setFieldValue}
                        handleBlur={handleBlur}
                        setFieldTouched={setFieldTouched}
                        selectedField={selectedEvent}
                      />
                    )}
                  {selectedEvent === Speeding_Functions.speeding && (
                    <Speeding
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      setFieldValue={setFieldValue}
                      handleBlur={handleBlur}
                      setFieldTouched={setFieldTouched}
                      selectedField={selectedEvent}
                    />
                  )}
                </div>
              </form>
            </div>
          </div>
        );
      }}
    </Formik>
  );
};

export default ScreenTrafficSignAndSignalDetection;
